import React, { useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Drawer } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingOutlined,
  TeamOutlined,
  ApartmentOutlined,
  ShareAltOutlined,
  UserOutlined,
  ShopOutlined,
  CreditCardOutlined,
  WalletOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  ProfileOutlined,
  EyeOutlined,
  CloudServerOutlined,
  NotificationOutlined,
  RollbackOutlined,
  BarChartOutlined,
  MessageOutlined,
  TrophyOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../store/authStore';

const { Sider } = Layout;

interface SidebarProps {
  isMobile: boolean;
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobile, open, onClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const menuItems = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表板' },
    { key: '/products', icon: <ShoppingOutlined />, label: '商品管理' },
    { key: '/groups', icon: <TeamOutlined />, label: '群组管理' },
    { key: '/group-monitor', icon: <EyeOutlined />, label: '群聊监控' },
    { key: '/traffic', icon: <BarChartOutlined />, label: '流量监控' },
    { key: '/users', icon: <UserOutlined />, label: '用户管理' },
    { key: '/merchants', icon: <ShopOutlined />, label: '商户管理' },
    { key: '/agents', icon: <ApartmentOutlined />, label: '代理中心' },
    { key: '/distribution', icon: <ShareAltOutlined />, label: '分销中心' },
    { key: '/ranking', icon: <TrophyOutlined />, label: '销量排行' },
    { key: '/payments', icon: <CreditCardOutlined />, label: '订单管理' },
    { key: '/channels', icon: <LineChartOutlined />, label: '通道分析' },
    { key: '/refunds', icon: <RollbackOutlined />, label: '退款记录' },
    { key: '/settlement', icon: <WalletOutlined />, label: '资金管理' },
    { key: '/customer-service', icon: <MessageOutlined />, label: '客服消息' },
    { key: '/broadcast', icon: <NotificationOutlined />, label: '消息广播' },
    { key: '/server', icon: <CloudServerOutlined />, label: '服务器管理' },
    { key: '/settings', icon: <SettingOutlined />, label: '系统设置' },
  ];

  const handleMenuClick = (key: string) => {
    navigate(key);
    if (isMobile) onClose();
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      void logout();
    } else if (key === 'profile') {
      navigate('/settings');
      if (isMobile) onClose();
    }
  };

  const userMenu = (
    <Menu
      onClick={handleUserMenuClick}
      items={[
        { key: 'profile', icon: <ProfileOutlined />, label: '个人设置' },
        { type: 'divider' },
        { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
      ]}
    />
  );

  // 侧边栏内容（桌面端和移动端共用）
  const siderContent = (
    <div className="flex flex-col h-full">
      {/* Logo 区域 */}
      <div className="flex items-center justify-center h-16 bg-primary-600 text-white">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
            <span className="text-primary-600 font-bold text-lg">TG</span>
          </div>
          {(!collapsed || isMobile) && (
            <span className="font-semibold text-lg">机器人管理</span>
          )}
        </div>
      </div>

      {/* 折叠按钮（仅桌面端） */}
      {!isMobile && (
        <div className="flex justify-center py-4 border-b border-gray-200">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="text-gray-600 hover:text-primary-500"
          />
        </div>
      )}

      {/* 菜单区域 */}
      <div className="flex-1 overflow-y-auto">
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => handleMenuClick(key)}
          className="border-r-0"
          inlineIndent={24}
        />
      </div>

      {/* 用户信息区域 */}
      <div className="border-t border-gray-200 p-4">
        <Dropdown overlay={userMenu} trigger={['click']} placement="topRight">
          <div className="flex items-center cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors">
            <Avatar
              src={user?.avatar}
              icon={<UserOutlined />}
              size={collapsed && !isMobile ? 'small' : 'default'}
            />
            {(!collapsed || isMobile) && (
              <div className="ml-3 flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {user?.username}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {user?.role === 'admin' ? '管理员' : '操作员'}
                </div>
              </div>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );

  // 移动端：使用 Drawer
  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={open}
        onClose={onClose}
        width={260}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ display: 'none' }}
        className="mobile-sidebar-drawer"
      >
        {siderContent}
      </Drawer>
    );
  }

  // 桌面端：使用 Sider
  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      className="layout-sider"
      width={240}
      collapsedWidth={80}
    >
      {siderContent}
    </Sider>
  );
};

export default Sidebar;
